/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FileText, RotateCcw, SendHorizontal, AlertTriangle, ExternalLink, Zap, MessageSquare, Mic } from 'lucide-react';
import Modal from '../Modal';
import MarkdownMessage from './MarkdownMessage';
import { aiAgentService } from '../../services/aiAgentService';
import { getApiErrorMessage } from '../../services/knowledgeBaseService';
import { usePermissions } from '../../context/PermissionContext';
import useVoiceAgent from '../../hooks/useVoiceAgent';
import { isVoiceAgentSupported } from '../../utils/voiceAgentClient';
import { VOICE_AGENT_ENABLED } from '../../constants/voiceAgent';
import VoiceComposer, { VoiceConversationStage } from './VoiceComposer';

const QUERY_MAX_LENGTH = 4000; // mirror server AI_AGENT_QUERY_MAX_LENGTH
const ERR_NO_ANSWER = 'The agent could not answer. Please try again.';

// Link to a cited PDF, opened at the page when there is one. Browser PDF viewers
// read the page from the `#page=N` fragment (a query param would be sent to S3
// instead). Anything other than an absolute http(s) URL is not linked.
const sourceHref = (url, page) => {
  if (!url || !/^https?:\/\//i.test(url)) { return null; }
  const pageNumber = Number.parseInt(page, 10);
  if (!Number.isFinite(pageNumber) || pageNumber < 1) { return url; }
  return `${url.split('#')[0]}#page=${pageNumber}`;
};

// "TAKE_PHOTO" -> "Take photo"
const actionLabel = (name) => {
  const words = String(name || '').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Action';
};

// Tooltip for an action chip: what the device says, plus any parameters.
const actionTitle = (a) => {
  const lines = [a.action];
  if (a.spoken) { lines.push(`Says: “${a.spoken}”`); }
  if (a.params && Object.keys(a.params).length) { lines.push(`Params: ${JSON.stringify(a.params)}`); }
  return lines.join('\n');
};

let messageSeq = 0;
const newMessage = (role, text, extra = {}) => ({ id: ++messageSeq, role, text, ...extra });

const TypingDots = () => (
  <span className="inline-flex items-center gap-1.5 py-1" aria-label="Agent is typing">
    <span className="w-1.5 h-1.5 rounded-full bg-base-content/40 animate-bounce [animation-delay:-0.3s]"></span>
    <span className="w-1.5 h-1.5 rounded-full bg-base-content/40 animate-bounce [animation-delay:-0.15s]"></span>
    <span className="w-1.5 h-1.5 rounded-full bg-base-content/40 animate-bounce"></span>
  </span>
);

/**
 * Chat preview for an AI agent, in a modal. The first message is sent without a
 * session; every reply's `sessionId` is sent with the next message so the agent
 * keeps the conversation context. "New chat" drops the session.
 *
 * Replies always stream: they appear as they are generated (SSE). Closing the
 * preview or starting a new chat cancels an answer that is still streaming.
 *
 * A Text / Voice switch picks the mode (Text whenever the preview opens).
 * "Voice" (when REACT_APP_VOICE_AGENT_ENABLED, the browser supports it and the
 * user has ai_agents.view) talks to the same agent over the voice WebSocket.
 * Until the first conversation connects, a centred start stage (`VoiceConversationStage`) takes
 * the place of the message list; once connected, the messages and the bottom
 * conversation bar (`VoiceComposer`) are shown: live transcripts become user bubbles, the spoken reply streams into an
 * assistant bubble, and talking over the agent interrupts it. In voice mode the
 * agent's greeting is not shown up front: the server speaks it once the conversation
 * connects, and it streams in like any reply (only once per conversation). See
 * docs/agent/modules/voice-agent.md (Frontend).
 */
const AgentChatPreview = ({ isOpen, onClose, agent = null, accountParams = {} }) => {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const [voiceMode, setVoiceMode] = useState(false);
  // Voice turn → bubble ids: user bubbles by turnId (with committed final text), replies by turnId.
  const voiceTurnsRef = useRef({ user: new Map(), agent: new Map() });
  // The server spoke the greeting in this conversation: later voice sessions skip it (metadata.skipGreeting).
  const [greeted, setGreeted] = useState(false);
  const { can } = usePermissions();
  const voiceAvailable = VOICE_AGENT_ENABLED && isVoiceAgentSupported() && can.ai_agents.view();

  const greeting = agent?.greetingMessage;

  const cancelStream = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  // Text mode shows the greeting right away; voice mode waits for the server to speak it.
  const resetChat = (useVoice = voiceMode) => {
    cancelStream();
    voice.stop();
    voiceTurnsRef.current = { user: new Map(), agent: new Map() };
    setGreeted(false);
    setSending(false);
    setMessages(greeting && !useVoice ? [newMessage('assistant', greeting, { greeting: true })] : []);
    setSessionId('');
    setDraft('');
  };

  // ---- voice mode ---------------------------------------------------------

  const upsertUserTurn = (turnId, patch) => {
    const turns = voiceTurnsRef.current.user;
    const existing = turns.get(turnId);
    if (existing) {
      updateMessage(existing.id, patch);
      return existing;
    }
    const message = newMessage('user', patch.text || '', { voice: true, interim: true, ...patch });
    const entry = { id: message.id, finals: '' };
    turns.set(turnId, entry);
    setMessages((m) => [...m.filter((msg) => !msg.error), message]);
    return entry;
  };

  const updateAgentTurn = (turnId, patch) => {
    const id = voiceTurnsRef.current.agent.get(turnId);
    if (id) { updateMessage(id, patch); }
  };

  /**
   * Turn ids restart at `turn-1` in every voice session, so the turn map must
   * not outlive one: settles bubbles left pending (interim text, typing dots)
   * and forgets the turns.
   */
  const endVoiceTurns = () => {
    const userIds = new Set([...voiceTurnsRef.current.user.values()].map((entry) => entry.id));
    const agentIds = new Set(voiceTurnsRef.current.agent.values());
    voiceTurnsRef.current = { user: new Map(), agent: new Map() };
    if (!userIds.size && !agentIds.size) { return; }
    setMessages((m) => m.flatMap((msg) => {
      if (userIds.has(msg.id) && msg.interim) {
        return msg.text ? [{ ...msg, interim: false }] : [];
      }
      if (agentIds.has(msg.id) && msg.streaming) {
        return msg.text || msg.actions?.length ? [{ ...msg, streaming: false }] : [];
      }
      return [msg];
    }));
  };

  const voiceHandlers = {
    'session.ready': () => endVoiceTurns(),
    'transcript.interim': ({ turnId, text }) => {
      const finals = voiceTurnsRef.current.user.get(turnId)?.finals || '';
      upsertUserTurn(turnId, { text: `${finals} ${text}`.trim(), interim: true });
    },
    'transcript.final': ({ turnId, text }) => {
      const entry = upsertUserTurn(turnId, {});
      entry.finals = `${entry.finals} ${text}`.trim();
      updateMessage(entry.id, { text: entry.finals, interim: true });
    },
    'user.turn.ended': ({ turnId, text }) => upsertUserTurn(turnId, { text, interim: false }),
    'agent.response.started': ({ turnId, kind }) => {
      const isGreeting = kind === 'greeting';
      if (isGreeting) { setGreeted(true); }
      const reply = newMessage('assistant', '', { voice: true, streaming: true, greeting: isGreeting });
      voiceTurnsRef.current.agent.set(turnId, reply.id);
      setMessages((m) => [...m, reply]);
    },
    'agent.text.delta': ({ turnId, text }) => updateAgentTurn(turnId, (msg) => ({ text: msg.text + text })),
    'agent.action': ({ turnId, action }) =>
      updateAgentTurn(turnId, (msg) => ({ actions: [...(msg.actions || []), action] })),
    'agent.text.completed': ({ turnId, text, interrupted, citations, actions }) => {
      const id = voiceTurnsRef.current.agent.get(turnId);
      if (!id) { return; }
      setMessages((m) => m.flatMap((msg) => {
        if (msg.id !== id) { return [msg]; }
        const finalText = text || msg.text;
        const finalActions = actions || msg.actions || [];
        // A failed reply (no text, no actions) leaves no empty bubble behind.
        if (!finalText && !finalActions.length) { return []; }
        return [{
          ...msg,
          text: finalText,
          actions: finalActions,
          ...(citations && { citations }),
          streaming: false,
          interrupted: interrupted || msg.interrupted,
        }];
      }));
    },
    'agent.interrupted': ({ turnId }) => updateAgentTurn(turnId, { streaming: false, interrupted: true }),
    // Non-fatal errors (e.g. the agent failed this turn); fatal ones end the session (shown in the composer).
    error: ({ message, fatal }) => {
      if (!fatal) { setMessages((m) => [...m, newMessage('assistant', message, { error: true, voice: true })]); }
    },
  };

  const voiceMetadata = useMemo(
    () => ({ agentId: agent?.id, ...accountParams, ...(greeted && { skipGreeting: true }) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agent?.id, accountParams?.accountId, greeted],
  );
  const voice = useVoiceAgent({ sessionMetadata: voiceMetadata, handlers: voiceHandlers });
  const voiceLive = voice.status === 'ready' && voice.micOn;
  // Until the first conversation connects, voice mode shows a centred start stage
  // instead of the (empty) message list and the bottom conversation bar.
  const showConversationStage = voiceMode && !voiceLive && messages.length === 0;

  // The voice session ended (ended by the user, closed or failed): settle its pending bubbles.
  // A conversation that had connected then starts over, back on the centred start stage
  // (which also shows the error that ended it, if any); the next one gets the greeting again.
  const wasLiveRef = useRef(false);
  useEffect(() => {
    if (voiceLive) { wasLiveRef.current = true; }
  }, [voiceLive]);
  useEffect(() => {
    if (voice.active) { return; }
    endVoiceTurns();
    if (wasLiveRef.current) {
      wasLiveRef.current = false;
      // Switching to Text also ends the session: keep the fresh Text chat that resetChat set up.
      if (voiceMode) {
        setGreeted(false);
        setMessages([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.active]);

  // Text and voice keep separate agent sessions, so switching mode starts a fresh conversation
  // (it also cancels a text stream / ends a voice conversation).
  const setMode = (useVoice) => {
    resetChat(useVoice);
    setVoiceMode(useVoice);
  };

  // Fresh conversation, in Text mode, every time the preview is opened; cancel streams on close/unmount.
  useEffect(() => {
    if (isOpen) { setVoiceMode(false); resetChat(false); } else { cancelStream(); voice.stop(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, agent?.id]);
  useEffect(() => () => cancelStream(), []);

  useEffect(() => {
    if (scrollRef.current) { scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }
  }, [messages, sending]);

  useEffect(() => {
    if (isOpen && !sending && !voiceMode) { inputRef.current?.focus(); }
  }, [isOpen, sending, voiceMode]);

  const updateMessage = (id, patch) => {
    setMessages((m) => m.map((msg) => (msg.id === id ? { ...msg, ...(typeof patch === 'function' ? patch(msg) : patch) } : msg)));
  };

  const completeReply = (id, reply) => {
    if (reply.sessionId) { setSessionId(reply.sessionId); }
    const actions = reply.actions || [];
    // An action-only reply has no answer text — show what the actions say instead.
    const spoken = actions.map((a) => a.spoken).filter(Boolean).join('\n\n');
    updateMessage(id, {
      text: reply.answer || spoken || '(No answer returned)',
      citations: reply.citations || [],
      actions,
      streaming: false,
    });
  };

  const failReply = (id, err, query) => {
    updateMessage(id, {
      text: getApiErrorMessage(err, ERR_NO_ANSWER),
      error: true,
      streaming: false,
      retryQuery: query,
    });
  };

  const send = async (text) => {
    const query = text.trim();
    if (!query || sending) { return; }
    const reply = newMessage('assistant', '', { streaming: true });
    setMessages((m) => [...m.filter((msg) => !msg.error), newMessage('user', query), reply]);
    setDraft('');
    setSending(true);
    const payload = { query, ...(sessionId && { sessionId }) };

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      try {
        const result = await aiAgentService.streamAiAgent(agent.id, payload, accountParams, {
          signal: controller.signal,
          onToken: (token) => updateMessage(reply.id, (msg) => ({ text: msg.text + token })),
          onAction: (action) => updateMessage(reply.id, (msg) => ({ actions: [...(msg.actions || []), action] })),
        });
        completeReply(reply.id, result);
      } catch (err) {
        if (err?.name === 'AbortError') { return; } // cancelled by the user
        // An expired session can't be refreshed from fetch — retry once without
        // streaming, where the API client refreshes the token automatically.
        if (err?.response?.status !== 401) { throw err; }
        const { data } = await aiAgentService.invokeAiAgent(agent.id, payload, accountParams);
        completeReply(reply.id, data?.data || {});
      } finally {
        if (abortRef.current === controller) { abortRef.current = null; }
      }
    } catch (err) {
      failReply(reply.id, err, query);
    } finally {
      setSending(false);
    }
  };

  const retry = (query) => {
    // Drop the failed exchange (the user message + the error) and resend it.
    setMessages((m) => {
      const withoutError = m.filter((msg) => !msg.error);
      const last = withoutError[withoutError.length - 1];
      return last?.role === 'user' && last.text === query ? withoutError.slice(0, -1) : withoutError;
    });
    send(query);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(draft);
    }
  };

  const handleClose = () => {
    cancelStream();
    voice.stop();
    onClose();
  };

  const renderBubbleContent = (m) => {
    if (m.role !== 'assistant' || m.error) { return m.text; }
    // Until answer text arrives, a streamed action's spoken line stands in for it.
    const text = m.text || (m.streaming ? (m.actions || []).map((a) => a.spoken).filter(Boolean).join('\n\n') : '');
    if (m.streaming && !text) { return <TypingDots />; }
    return (
      <>
        {/* Agent answers (and the greeting) may be Markdown; user text and errors stay plain. */}
        <MarkdownMessage text={text} />
        {m.streaming && <span className="inline-block w-1.5 h-4 -mb-0.5 bg-base-content/50 animate-pulse" aria-hidden="true" />}
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnBackdropClick={false}
      title={`Preview — ${agent?.name || 'AI Agent'}`}
      className="max-w-2xl w-full overflow-hidden"
    >
      <div className="flex flex-col h-[65vh] -m-6">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 px-5 py-2.5 border-b border-base-content/10 bg-base-200/40">
          <p className="text-[11px] text-base-content/50 font-medium truncate">
            {agent?.model ? <>Model <span className="font-mono">{agent.model}</span> · </> : null}
            {voiceMode ? 'Voice conversation' : sessionId ? 'Conversation in progress' : 'New conversation'}
          </p>
          <div className="flex items-center gap-3 shrink-0">
            {/* Voice mode has no New chat: each voice session is its own conversation, ended with the end button. */}
            {!voiceMode && (
              <button
                type="button"
                onClick={() => resetChat()}
                disabled={!sessionId && messages.every((m) => m.greeting) && !sending}
                className="btn btn-xs btn-ghost border border-base-content/10 rounded-lg gap-1.5 font-bold"
              >
                <RotateCcw size={12} />
                <span>New chat</span>
              </button>
            )}
            {/* Text / Voice mode switch (Text by default), last in the toolbar. */}
            {voiceAvailable && (
              <div
                role="radiogroup"
                aria-label="Conversation mode"
                className={`inline-flex items-center p-0.5 rounded-lg bg-base-300/60 ${sending ? 'opacity-50' : ''}`}
              >
                {[
                  { value: false, label: 'Text', Icon: MessageSquare, title: 'Type messages to the agent' },
                  { value: true, label: 'Voice', Icon: Mic, title: 'Talk to the agent with your microphone' },
                ].map(({ value, label, Icon, title }) => {
                  const selected = voiceMode === value;
                  return (
                    <button
                      key={label}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      title={title}
                      disabled={sending}
                      onClick={() => { if (!selected) { setMode(value); } }}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed ${
                        selected ? 'bg-base-100 text-primary shadow-sm' : 'text-base-content/60 hover:text-base-content'
                      }`}
                    >
                      <Icon size={12} />
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {showConversationStage ? (
          <div className="flex-1 min-h-0">
            <VoiceConversationStage voice={voice} agentName={agent?.name} />
          </div>
        ) : (
        <>
        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" aria-live="polite">
          {messages.length === 0 && !sending && (
            <div className="h-full flex items-center justify-center text-center">
              <p className="text-xs text-base-content/40 max-w-xs">
                {voiceMode
                  ? 'Connected — start speaking. The agent will answer here.'
                  : <>Send a message to see how this agent responds. Answers use the agent&apos;s prompt and linked knowledge bases.</>}
              </p>
            </div>
          )}

          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] space-y-1.5 ${m.role === 'user' ? 'items-end' : 'items-start'} flex flex-col`}>
                <div
                  className={`px-4 py-2.5 rounded-2xl text-sm break-words ${
                    m.role === 'user'
                      ? `bg-primary text-primary-content rounded-br-md whitespace-pre-wrap${m.interim ? ' opacity-70 italic' : ''}`
                      : m.error
                        ? 'bg-error/10 text-error border border-error/20 rounded-bl-md whitespace-pre-wrap'
                        : 'bg-base-200 text-base-content rounded-bl-md min-w-0 max-w-full'
                  }`}
                >
                  {m.error && <AlertTriangle size={14} className="inline mr-1.5 -mt-0.5" />}
                  {renderBubbleContent(m)}
                </div>

                {m.interrupted && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-base-content/40">Interrupted</span>
                )}

                {m.error && m.retryQuery && (
                  <button
                    type="button"
                    onClick={() => retry(m.retryQuery)}
                    disabled={sending}
                    className="text-[11px] font-bold text-error hover:underline"
                  >
                    Retry
                  </button>
                )}

                {/* Device actions the reply asks for (e.g. TAKE_PHOTO). Shown only — the preview does not run them. */}
                {m.actions?.length > 0 && (
                  <div className="space-y-1 pt-0.5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40">Actions</p>
                    <div className="flex flex-wrap items-center gap-1">
                      {m.actions.map((a, idx) => (
                        <span
                          key={a.id || `${a.action}-${idx}`}
                          title={actionTitle(a)}
                          className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-secondary/10 text-secondary border-secondary/20"
                        >
                          <Zap size={10} className="shrink-0" />
                          <span className="truncate max-w-[16rem]">{actionLabel(a.action)}</span>
                          {a.requiresConfirmation && (
                            <span className="shrink-0 px-1.5 rounded-full bg-warning/15 text-warning text-[10px] font-bold">
                              Needs confirmation
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sources: one badge per cited page ("document · p. N"); a document without pages gets one badge.
                    With a `documentUrl` the badge opens the PDF in a new tab at that page. */}
                {m.citations?.length > 0 && (
                  <div className="space-y-1 pt-0.5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40">Sources</p>
                    <div className="flex flex-wrap items-center gap-1">
                      {m.citations.flatMap((c, idx) => {
                        const docKey = `${c.documentId || c.label}-${idx}`;
                        const pages = c.pages?.length ? c.pages : [null];
                        return pages.map((page) => {
                          const href = sourceHref(c.documentUrl, page);
                          const Tag = href ? 'a' : 'span';
                          return (
                            <Tag
                              key={`${docKey}-${page ?? 'doc'}`}
                              {...(href && { href, target: '_blank', rel: 'noopener noreferrer' })}
                              title={`${c.label}${page ? ` — page ${page}` : ''}${c.snippet ? `\n${c.snippet}` : ''}`}
                              className={`inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-primary/10 text-primary border-primary/20${
                                href ? ' hover:bg-primary/20 hover:underline transition-colors' : ''
                              }`}
                            >
                              <FileText size={10} className="shrink-0" />
                              <span className="truncate max-w-[16rem]">{c.label}</span>
                              {page && <span className="shrink-0 text-primary/70">· p. {page}</span>}
                              {href && <ExternalLink size={10} className="shrink-0 text-primary/70" />}
                            </Tag>
                          );
                        });
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Composer */}
        {voiceMode ? (
          <VoiceComposer voice={voice} />
        ) : (
        <form
          onSubmit={(e) => { e.preventDefault(); send(draft); }}
          className="flex items-end gap-2 px-4 py-3 border-t border-base-content/10"
        >
          <textarea
            ref={inputRef}
            rows={1}
            maxLength={QUERY_MAX_LENGTH}
            placeholder="Type a message… (Enter to send, Shift+Enter for a new line)"
            value={draft}
            disabled={sending}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 max-h-32 min-h-[2.75rem] px-4 py-2.5 bg-base-100 border border-base-300 rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={sending || !draft.trim()}
            className="btn btn-primary rounded-xl h-11 min-h-0 px-4"
            aria-label="Send message"
          >
            <SendHorizontal size={16} />
          </button>
        </form>
        )}
        </>
        )}
      </div>
    </Modal>
  );
};

AgentChatPreview.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  agent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    model: PropTypes.string,
    greetingMessage: PropTypes.string,
  }),
  // `{ accountId }` in the platform-wide view, `{}` otherwise.
  accountParams: PropTypes.object,
};

export default AgentChatPreview;
